import { portalText } from "../i18n/portal.js";
/**
 * Messages a caller left, in History and in the notification centre — plan-018 unit **WAPP-B** (design §30.4.2,
 * §30.6.18, §30.12; ADR 0060), over API-MSG's fields:
 *
 *   GET /v1/conversations            each row's `has_taken_message`; `?outcome=message_taken` is the "Messages" filter
 *   GET /v1/conversations/{id}       `taken_message: TakenMessageView[]` (oldest first) and, for a conversation in
 *                                    which the caller asked for a person, `handoff` (its latest attempt)
 *   GET /v1/notifications            derived rows of kind `taken_message.recorded` / `taken_message.person_requested`
 *
 * WHO READS WHAT (API-MSG). Every reader of the conversation learns that a message was left, what led to it, the kind
 * of contact, whether it was confirmed and when. The name, the contact, the message and the summary are read only by a
 * role that reads transcripts (`withheld`); a calling number only by a role that reads caller details
 * (`contact_withheld`). The screen says "hidden for your role" for each — never an empty field that reads as "the caller
 * gave none".
 *
 * THE NOTICE IS ENGLISH ON THE WIRE (ADR 0051): the API words its title and body in English, naming the VoiceAgent only.
 * The portal recognises the four formats it registers (`apps/api/src/routes/notification/route.ts`) and words them in the
 * person's language; a title it does not recognise is shown as the API wrote it.
 *
 * The fields are OPTIONAL on the views because the in-memory gateway builds its own literals for every other
 * conversation; the api gateway always fills them.
 */
import { MalformedResponseError, readArray, readBoolean, readRecord, readText } from "./decode.js";

/** What led to a message (`taken_message.offered_by`). */
export type TakenMessageOfferedBy = "in_conversation" | "handoff" | "after_hours" | "overflow";

/** How the caller can be reached (`taken_message.contact_kind`). */
export type TakenMessageContactKind = "calling_number" | "phone" | "email";

/** One message a caller left (`TakenMessageView`). */
export interface TakenMessageView {
  readonly takenMessageId: string;
  readonly offeredBy: TakenMessageOfferedBy;
  readonly callerName: string | null;
  readonly contactKind: TakenMessageContactKind | null;
  readonly contactValue: string | null;
  /** `false` when a spoken contact did not match what was recognised — "not confirmed". */
  readonly contactVerified: boolean;
  readonly messageText: string | null;
  readonly handoffSummary: string | null;
  readonly createdAt: string;
  /** Retention removed the name, the contact, the message and the summary. */
  readonly blanked: boolean;
  /** The reader's role does not read transcripts: the caller's words and contact are `null`. */
  readonly withheld: boolean;
  /** The contact's value is `null` for this reader (withheld, or a calling number for a role without caller details). */
  readonly contactWithheld: boolean;
}

/** How the caller's request for a person went (`ConversationHandoff.outcome`). */
export type ConversationHandoffOutcome = "in_progress" | "joined" | "not_joined" | "priority_message";

/** A conversation in which the caller asked for a person: its latest attempt (`ConversationHandoff`). */
export interface ConversationHandoffView {
  readonly outcome: ConversationHandoffOutcome;
  /** How the person was called; `null` for a priority message, which calls nobody. */
  readonly path: "sip_trunk" | "carrier" | null;
  /** What the person heard before joining; `null` until joined, once blanked, or for a role without transcripts. */
  readonly summary: string | null;
  readonly joinedAt: string | null;
}

declare module "./gateway.js" {
  interface PastConversationRowView {
    /** plan-018 API-MSG `has_taken_message`: a caller left a structured message in this conversation. */
    readonly hasTakenMessage?: boolean;
  }
  interface PastConversationView {
    /** plan-018 API-MSG `taken_message`: the messages a caller left, oldest first. */
    readonly takenMessage?: readonly TakenMessageView[];
    /** plan-018 API-MSG `handoff`: present only when the caller asked for a person. */
    readonly handoff?: ConversationHandoffView | null;
  }
}

/** History's "Messages" chip: the API's `outcome=message_taken` — a code, never an agent_template's word. */
export const TAKEN_MESSAGE_OUTCOME_FILTER = "message_taken";

const OFFERED_BY: readonly TakenMessageOfferedBy[] = ["in_conversation", "handoff", "after_hours", "overflow"];
const CONTACT_KIND: readonly TakenMessageContactKind[] = ["calling_number", "phone", "email"];
const HANDOFF_OUTCOME: readonly ConversationHandoffOutcome[] = ["in_progress", "joined", "not_joined", "priority_message"];
const HANDOFF_PATH: readonly ("sip_trunk" | "carrier")[] = ["sip_trunk", "carrier"];

function oneOf<T extends string>(known: readonly T[], value: unknown, path: string, wanted: string): T {
  const raw = readText(value, path);
  const found = known.find((one) => one === raw);
  if (found === undefined) throw new MalformedResponseError(path, wanted, raw);
  return found;
}

function nullableText(value: unknown, path: string): string | null {
  return value === null ? null : readText(value, path);
}

/** One `TakenMessageView`. */
export function takenMessageOfWire(value: unknown, path: string): TakenMessageView {
  const row = readRecord(value, path);
  return {
    takenMessageId: readText(row["taken_message_id"], `${path}.taken_message_id`),
    offeredBy: oneOf(OFFERED_BY, row["offered_by"], `${path}.offered_by`, "a documented offered_by"),
    callerName: nullableText(row["caller_name"], `${path}.caller_name`),
    contactKind:
      row["contact_kind"] === null ? null : oneOf(CONTACT_KIND, row["contact_kind"], `${path}.contact_kind`, "a contact kind"),
    contactValue: nullableText(row["contact_value"], `${path}.contact_value`),
    contactVerified: readBoolean(row["contact_verified"], `${path}.contact_verified`),
    messageText: nullableText(row["message_text"], `${path}.message_text`),
    handoffSummary: nullableText(row["handoff_summary"], `${path}.handoff_summary`),
    createdAt: readText(row["created_at"], `${path}.created_at`),
    blanked: readBoolean(row["blanked"], `${path}.blanked`),
    withheld: readBoolean(row["withheld"], `${path}.withheld`),
    contactWithheld: readBoolean(row["contact_withheld"], `${path}.contact_withheld`),
  };
}

/** `taken_message` on a conversation's detail; absent from an api before plan-018 (left undefined). */
export function takenMessageListOfWire(value: unknown, path: string): readonly TakenMessageView[] | undefined {
  if (value === undefined) return undefined;
  return readArray(value, path).map((one, index) => takenMessageOfWire(one, `${path}[${String(index)}]`));
}

/** `handoff` on a conversation's detail: absent (or `null`) when the caller never asked for a person. */
export function conversationHandoffOfWire(value: unknown, path: string): ConversationHandoffView | null {
  if (value === undefined || value === null) return null;
  const row = readRecord(value, path);
  return {
    outcome: oneOf(HANDOFF_OUTCOME, row["outcome"], `${path}.outcome`, "a handoff outcome"),
    path: row["path"] === null ? null : oneOf(HANDOFF_PATH, row["path"], `${path}.path`, "sip_trunk or carrier"),
    summary: nullableText(row["summary"], `${path}.summary`),
    joinedAt: nullableText(row["joined_at"], `${path}.joined_at`),
  };
}

/** `has_taken_message` on a History row; absent from an api before plan-018 (left undefined). */
export function hasTakenMessageOfWire(value: unknown, path: string): boolean | undefined {
  return value === undefined ? undefined : readBoolean(value, path);
}

// --- The notice -------------------------------------------------------------------------------------------------

/** The two notice kinds API-MSG derives from `taken_message` (the schema's kind pattern needs a dot). */
export const TAKEN_MESSAGE_NOTICE_KIND = ["taken_message.recorded", "taken_message.person_requested"] as const;

export type TakenMessageNoticeKind = (typeof TAKEN_MESSAGE_NOTICE_KIND)[number];

export function isTakenMessageNoticeKind(kind: string): kind is TakenMessageNoticeKind {
  return (TAKEN_MESSAGE_NOTICE_KIND as readonly string[]).includes(kind);
}

/** The API's English title formats (route.ts), each with the VoiceAgent's name after it. */
const TITLE_PREFIX: Readonly<Record<TakenMessageNoticeKind, string>> = {
  "taken_message.recorded": "New message for ",
  "taken_message.person_requested": "Someone asked to speak to a person — ",
};

/** "New message for Front desk" in the person's language, or the API's title when it is not one of the two formats. */
export function takenMessageNoticeTitleOf(kind: TakenMessageNoticeKind, title: string): string {
  const prefix = TITLE_PREFIX[kind];
  if (!title.startsWith(prefix) || title.length === prefix.length) return title;
  const name = title.slice(prefix.length);
  return kind === "taken_message.recorded"
    ? portalText("operations", "taken_message.notice_title", { v0: name })
    : portalText("operations", "taken_message.notice_title_person", { v0: name });
}

/** The API's four English bodies (route.ts), one per `offered_by`, each naming nobody. */
const BODY_KEY = [
  ["A caller asked for a person and left a way to reach them. Open the conversation to read it.", "taken_message.notice_body_handoff"],
  ["Left after hours. Open the conversation to read it.", "taken_message.notice_body_after_hours"],
  ["Left while every line was busy. Open the conversation to read it.", "taken_message.notice_body_overflow"],
  ["A caller left a message. Open the conversation to read it.", "taken_message.notice_body"],
] as const;

/** The notice's body in the person's language, or the API's body when it is not one of the four. */
export function takenMessageNoticeBodyOf(body: string): string {
  const found = BODY_KEY.find(([english]) => english === body);
  return found === undefined ? body : portalText("operations", found[1]);
}

/**
 * Where the notice leads: the conversation in its VoiceAgent's History, with the "Messages" filter on and every date,
 * so the conversation is in the list whatever History's last window was. The API's link is
 * `/agents/{voice_agent_id}?tab=history&conversation={conversation_id}`; anything else is kept as it is.
 */
export function takenMessageNoticeLinkOf(linkPath: string | null): string | null {
  if (linkPath === null) return null;
  const [pathname, query] = linkPath.split("?", 2);
  if (pathname === undefined || query === undefined || !/^\/agents\/[^/]+$/.test(pathname)) return linkPath;
  const search = new URLSearchParams(query);
  const conversation = search.get("conversation");
  if (search.get("tab") !== "history" || conversation === null) return linkPath;
  const next = new URLSearchParams({ tab: "history", range: "all", outcome: TAKEN_MESSAGE_OUTCOME_FILTER, conversation });
  return `${pathname}?${next.toString()}`;
}
